import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { 
  History, 
  Loader2,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  XCircle,
  Image,
  FileText,
  Receipt,
  Clock,
  Scissors,
  ListTodo,
  Filter,
  ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('all');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const limit = 30;

  const fetchHistory = useCallback(async (reset = false) => {
    try {
      const currentOffset = reset ? 0 : offset;
      let url = `/history?limit=${limit}&offset=${currentOffset}`;
      
      if (entityFilter !== 'all') {
        url += `&entity_type=${entityFilter}`;
      }
      
      const [historyRes, statsRes] = await Promise.all([
        api.get(url),
        reset || currentOffset === 0 ? api.get('/history/stats') : Promise.resolve({ data: stats })
      ]);
      
      if (reset) {
        setHistory(historyRes.data);
        setOffset(limit);
      } else {
        setHistory(prev => [...prev, ...historyRes.data]);
        setOffset(currentOffset + limit);
      }
      
      setHasMore(historyRes.data.length === limit);
      
      if (statsRes.data) {
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch history:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [entityFilter, offset, stats]);

  useEffect(() => {
    setLoading(true);
    setOffset(0);
    fetchHistory(true);
  }, [entityFilter]);

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      setLoadingMore(true);
      fetchHistory(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'created':
        return <Plus className="w-4 h-4" />;
      case 'updated':
        return <Edit className="w-4 h-4" />;
      case 'deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'uncompleted':
        return <XCircle className="w-4 h-4" />;
      default:
        return <History className="w-4 h-4" />;
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'cosplay':
        return <Scissors className="w-4 h-4" />;
      case 'task':
        return <ListTodo className="w-4 h-4" />;
      case 'note':
        return <FileText className="w-4 h-4" />;
      case 'photo':
        return <Image className="w-4 h-4" />;
      case 'receipt':
        return <Receipt className="w-4 h-4" />;
      case 'time_log':
        return <Clock className="w-4 h-4" />;
      default:
        return <History className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const groupByDate = (items) => {
    const groups = {};
    items.forEach(item => {
      const date = new Date(item.created_at);
      const key = date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'long', 
        day: 'numeric',
        year: 'numeric'
      });
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(item);
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const groupedHistory = groupByDate(history);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">History</h1>
          <p className="mt-1">Track all changes to your cosplays</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="win-raised p-4">
            <p className="text-2xl font-bold font-mono">{stats.total}</p>
            <p className="text-xs mt-1">Total Events</p>
          </div>
          <div className="win-raised p-4">
            <p className="text-2xl font-bold font-mono">{stats.by_action?.created || 0}</p>
            <p className="text-xs mt-1">Created</p>
          </div>
          <div className="win-raised p-4">
            <p className="text-2xl font-bold font-mono">{stats.by_action?.updated || 0}</p>
            <p className="text-xs mt-1">Updated</p>
          </div>
          <div className="win-raised p-4">
            <p className="text-2xl font-bold font-mono">{stats.by_action?.deleted || 0}</p>
            <p className="text-xs mt-1">Deleted</p>
          </div>
          <div className="win-raised p-4">
            <p className="text-2xl font-bold font-mono">{stats.by_action?.completed || 0}</p>
            <p className="text-xs mt-1">Completed</p>
          </div>
          <div className="win-raised p-4">
            <p className="text-2xl font-bold font-mono">{stats.by_action?.uncompleted || 0}</p>
            <p className="text-xs mt-1">Uncompleted</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <Filter className="w-5 h-5" />
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-48" data-testid="entity-filter">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="cosplay">Cosplays</SelectItem>
            <SelectItem value="task">Tasks</SelectItem>
            <SelectItem value="note">Notes</SelectItem>
            <SelectItem value="photo">Photos</SelectItem>
            <SelectItem value="receipt">Receipts</SelectItem>
            <SelectItem value="time_log">Time Logs</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* History Timeline */}
      {history.length === 0 ? (
        <div className="win-well p-12 text-center">
          <History className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-lg font-heading font-semibold mb-2">No History Yet</h3>
          <p className="mb-6">
            {entityFilter !== 'all'
              ? `No ${entityFilter.replace('_', ' ')} events recorded yet.`
              : 'Start creating cosplays and tracking your progress!'}
          </p>
          <Link to="/cosplays">
            <Button className="font-semibold gap-2">
              <Plus className="w-5 h-5" />
              Create Cosplay
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedHistory).map(([date, items]) => (
            <div key={date}>
              <h3 className="text-sm font-medium mb-4 sticky top-0 bg-[var(--win-face)] py-2 -mx-2 px-2 z-10">
                {date}
              </h3>
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="win-raised p-4 group"
                    data-testid={`history-item-${item.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Action Icon */}
                      <div className="w-10 h-10 win-well flex items-center justify-center flex-shrink-0">
                        {getActionIcon(item.action)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium">
                            {item.action}
                          </span>
                          <span className="flex items-center gap-1 win-well px-2 py-0.5 text-xs">
                            {getEntityIcon(item.entity_type)}
                            {item.entity_type.replace('_', ' ')}
                          </span>
                          <span className="text-xs ml-auto">
                            {formatDate(item.created_at)}
                          </span>
                        </div>

                        <p className="text-sm">
                          {item.details || `${item.action} ${item.entity_type.replace('_', ' ')}: ${item.entity_name || 'Unknown'}`}
                        </p>

                        {item.cosplay_title && item.cosplay_id && (
                          <Link
                            to={`/cosplays/${item.cosplay_id}`}
                            className="inline-flex items-center gap-1 text-xs mt-2"
                          >
                            <Scissors className="w-3 h-3" />
                            {item.cosplay_title}
                          </Link>
                        )}

                        {/* Show changes if available */}
                        {item.changes && Object.keys(item.changes).length > 0 && (
                          <div className="mt-2 win-well p-2 text-xs">
                            {Object.entries(item.changes).map(([key, value]) => (
                              <div key={key} className="flex items-center gap-2">
                                <span className="font-medium">{key}:</span>
                                <span className="line-through">{String(value.old)}</span>
                                <span>→</span>
                                <span>{String(value.new)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Load More */}
          {hasMore && (
            <div className="flex justify-center pt-4">
              <Button
                onClick={loadMore}
                disabled={loadingMore}
                variant="outline"
                className="gap-2"
                data-testid="load-more-btn"
              >
                {loadingMore ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <ChevronDown className="w-5 h-5" />
                    Load More
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryPage;
